import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { JanusService } from '@shared/services/janus.service';
import { StateService } from '@shared/services/state.service';
import { MediaService } from '@shared/services/media.service';
import { AuthService } from '@shared/services/auth.service';
import { User } from '../models/message';
import { TokenService } from '@shared/services/token.service';
import { BehaviorSubject, Subject } from 'rxjs';
import { CustomizationService } from '../services/customization.service';
import { CustomizationModel } from '../models/customization.model';
import { RealtimeDatabaseService } from '../services/rtdb.service';
import {
  auditTime,
  distinctUntilChanged,
  filter, first,
  map,
  skip,
  takeUntil, tap
} from 'rxjs/operators';
import { UserStatus } from '../models/user-status';
import { ErrorModel } from '../models/error.model';
import { RemoteRoom } from '../models/remote-room';
import { PlatformDetectorService } from '@shared/services/platform-detector/platform-detector.service';
import { environment } from '../../../environments/environment';
import { NotificationService } from '@shared/services/notification/notification.service';
import { AppService } from '@shared/services/app.service';
import { MultiMedia } from '@models';
import { SoundService } from '@shared/services/sound.service';
import { RealtimeDatabaseSubscriptionsService } from '../services/rtdb-subscriptions.service';

@Component({
  selector: 'stusan-room',
  templateUrl: './room.component.html',
  styleUrls: ['./room.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RoomComponent implements OnInit, OnDestroy {
  public handsState$: BehaviorSubject<UserStatus[]> = new BehaviorSubject<UserStatus[]>([]);
  public micState$: BehaviorSubject<UserStatus> = new BehaviorSubject<UserStatus>({});
  isLoggedIn: boolean | null = this.stateService.isLoggedIn;
  connecting = true;
  public customConfig: CustomizationModel;
  private readonly destroyed$ = new Subject<boolean>();
  public isRecordingBot = false;
  public hasMediaPermission = true;
  private roomName: string;
  private isSafariDesktop: boolean;

  constructor(
    private readonly router: Router,
    public readonly stateService: StateService,
    public readonly janusService: JanusService,
    private readonly route: ActivatedRoute,
    private readonly cdr: ChangeDetectorRef,
    private media: MediaService,
    public auth: AuthService,
    private readonly tokenService: TokenService,
    public customizationService: CustomizationService,
    private readonly rtdb: RealtimeDatabaseService,
    private platformDetectorService: PlatformDetectorService,
    private readonly notificationService: NotificationService,
    public readonly appService: AppService,
    private soundService: SoundService,
    private readonly rtdbSubscriptionsService: RealtimeDatabaseSubscriptionsService,
  ) {
    this.customConfig = customizationService.config;
  }

  async ngOnInit(): Promise<void> {
    this.isSafariDesktop = (this.platformDetectorService.isBrowserSafari() && this.platformDetectorService.isDesktop());
    const token = this.route.snapshot.paramMap.get('token') as string;
    const roomName = this.route.snapshot.paramMap.get('roomName') as string;
    this.stateService.roomName = roomName;
    this.roomName = roomName;
    const user: User | null = await this.tokenService.parseToken(token, roomName);
    if (user?.displayName) {
      this.stateService.userName = user?.displayName as string;
      this.stateService.isLoggedIn = true;
    }
    if (user?.role === 'participant') {
      this.stateService.videoEnabled = false;
      this.stateService.audioEnabled = false;
    }
    // this.rtdbSubscriptionsService.usersSubscription$
    //   .pipe(
    //     takeUntil(this.destroyed$),
    //     distinctUntilChanged(),
    //     map(items => items ? Object.keys(items).map(key => ({
    //       isRaisedHand: items[key].isRaisedHand,
    //       displayName: items[key].displayName,
    //       id: key
    //     })) : []),
    //     map(users => users.filter(item => item.isRaisedHand && item.displayName)),
    //     map(users => users.filter(item => item.id !== this.stateService.userId)),
    //   ).subscribe(this.handsState$);
    // this.handsState$.pipe(
    //   takeUntil(this.destroyed$),
    //   distinctUntilChanged(),
    //   skip(1), // skip initial value
    // ).subscribe((handsState) => {
    //   this.cdr.markForCheck();
    // });

    this.rtdbSubscriptionsService.micStateSubscription$.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
      skip(1), // skip initial value
    ).subscribe((micState) => {
      console.log('room micState$ updated', micState);
      this.janusService.sendAudio(!this.stateService.audioEnabled);
      this.cdr.markForCheck();
    });

    this.initIsRecordingBotStatus();

    if (this.isRecordingBot) {
      this.stateService.isLoggedIn = true;
    } else {
      this.handleBrowserPermission();
    }

    this.isLoggedIn = this.stateService.isLoggedIn;

    this.janusService.connection$.subscribe((connected) => {
      this.connecting = !connected;
      this.cdr.markForCheck();
    });


    // password to janus room
    const pin: string | undefined = this.route.snapshot.queryParams.userToken;

    this.setUserNameFromQueryParams();

    // TODO: use canLoad
    if (!this.isLoggedIn && environment.signupFlowEnabled) {
      this.router.navigate(['/']).then();

      return;
    }

    if (!this.isLoggedIn) {
      const queryParams: { [key: string]: string } = {};

      if (this.route.snapshot.queryParams.userToken) {
        queryParams.userToken = this.route.snapshot.queryParams.userToken;
      }

      this.router.navigate(['/login', roomName], { queryParams, skipLocationChange: true }).then();

      return;
    }
    const userName: any = this.stateService.userName || 'You';
    this.janusService.getRoomJoinInfo(user?.webinarId ? user?.webinarId : this.stateService.roomName).pipe(
      takeUntil(this.destroyed$),
    ).subscribe((room: RemoteRoom) => {
      this.stateService.roomId = room.janusRoomId;
      this.stateService.webinarName = room.name;
      this.appService.janusUrl = room.janusWsUrl;
      this.tokenService.isHost$.pipe(
        takeUntil(this.destroyed$)
      ).subscribe(isHost => {
        this.stateService.isRecordingEnabled = !!(isHost && room.isRecordingEnabled);
      });
      this.janusService.joinRoom(room.janusRoomId, userName);

      if (this.isSafariDesktop) {
        this.janusService.joinRoom(room.janusRoomId, userName + ' screen', '', true);
      }

      this.janusService.videos$.asObservable().pipe(
        takeUntil(this.destroyed$),
        auditTime(500),
        distinctUntilChanged(),
        first()
      ).subscribe((videos: MultiMedia[]) => {
        if (videos.length < 5 && !this.stateService.isRecordingBot) {
          this.soundService.playJoin(); // You Entered the room
        }
      });
    }, (error: ErrorModel) => {
      if (error.code) {
        this.router.navigate(['/error'], {
          queryParams: {
            code: error.code,
            message: JSON.stringify(error.message),
          }
        }).then();
      } else {
        this.notificationService.showError('API server is not available.');
      }
    });

    // this.media.getMediaStream().then((stream: MediaStream) => {
    //   this.voiceActivityDetect(stream);
    //   const videoTracks = stream.getVideoTracks();
    //   videoTracks.forEach((track) => {
    //     track.stop();
    //   });
    // });
    // this.stateService.isVoiceActive$.pipe(
    //   takeUntil(this.destroyed$),
    //   take(3),
    //   delay(3000)
    // ).subscribe((isVoiceActive) => {
    //   if (isVoiceActive && !this.stateService.audioEnabled) {
    //     this.voiceActivityMessage();
    //   }
    // });
    // this.stateService.isVoiceActive$.pipe(
    //   takeUntil(this.destroyed$),
    //   auditTime(30000),
    //   distinctUntilChanged()
    // ).subscribe((isVoiceActive) => {
    //   if (isVoiceActive && !this.stateService.audioEnabled) {
    //     this.voiceActivityMessage();
    //   }
    // });
  }

  // voiceActivityMessage() {
  //   this.isVoiceActive = true;
  //   this.notificationService.showInfo('We noticed you\'re speaking now. Turn on your microphone to be heard.');
  // }

  public firstChar(displayName: string | undefined): string | undefined {
    const name = displayName as any;
    return displayName && displayName.charAt(0);
  }

  // async hideRaisedHand(id: string | undefined): Promise<any> {
  //   await this.rtdbSubscriptionsService.hideRaisedHand(id as string);
  // }

  onMediaPermissionChange(permission: boolean) {
    this.hasMediaPermission = permission;
  }

  private setUserNameFromQueryParams(): void {
    if (this.route.snapshot.queryParams.userName) {
      this.stateService.userName = this.route.snapshot.queryParams.userName;
    }
  }

  private initIsRecordingBotStatus(): void {
    this.isRecordingBot = this.route.snapshot.queryParamMap?.get('recordingBot') === 'true';
    this.stateService.isRecordingBot = this.isRecordingBot;
  }


  // The case when user switches cam or mic off then trying to reload and reconnect to live session
  // without this method user will see infinite loading
  private handleBrowserPermission() {
    const canBeReload = this.stateService.getSavedState('canBeReload', 'boolean');
    if (canBeReload) {
      this.stateService.saveState('canBeReload', false);
      window.location.reload();
    }
    this.media.devicesGetUserMedia().subscribe(hasPermission => {
      this.hasMediaPermission = hasPermission;
      if (!this.hasMediaPermission) {
        const queryParams: { [key: string]: string } = {};
        if (this.route.snapshot.queryParams.token) {
          queryParams.token = this.route.snapshot.queryParams.token;
        }

        this.router.navigate(['/'], { queryParams }).then(() => this.stateService.saveState('canBeReload', true));
      }
    });
  }

  trackByMethod(index: number, el: any): number {
    return el.id;
  }

  toggleMobileMenu(event: Event) {
    this.stateService.isMobileMenuHidden = !this.stateService.isMobileMenuHidden;
  }

  ngOnDestroy(): void {
    this.stateService.isLoggedIn = false;
    this.stateService.userName = null;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }
}
